import {
  activeCountry,
  regionId,
} from "@components/WalletForm/models/products.ts";
import { amplitude } from "@lib/amplitude";
import { getGA4ClientId } from "@lib/analytics/getGAClientId.ts";
import { getInitialAnalyticsData, getUTMs } from "@lib/analytics/UTMs.ts";
import type { CartUpdateParams } from "@lib/store/types/entities/cart.type.ts";
import * as Sentry from "@sentry/astro";
import CFG from "../../store.config";
import type { ProductsCollection } from "../../types";
import api from "./api.ts";
import Cart from "./cart.ts";
import { getCookie } from "@lib/util.ts";
import {
  AFFILIATES_COOKIES_CLICK_ID,
  AFFILIATES_COOKIES_CLICK_KEY,
} from "@lib/analytics/constants.ts";

export class Store {
  private _cart = new Cart();

  get cart() {
    return this._cart;
  }

  async regions() {
    const { regions } = await api.regions();
    return regions;
  }

  async shippingOptions() {
    const { shipping_options } = await api.shippingOptions();
    return shipping_options;
  }

  async initCart() {
    await this._cart.initialize();
    await this.clearCart();
    return this._cart.original;
  }

  createCart() {
    let device_id: string | undefined;
    let session_id: number | undefined;
    try {
      device_id = amplitude.getDeviceId();
      session_id = amplitude.getSessionId();
    } catch (e) {
      //
    }
    return this._cart.createCart({
      context: {
        device_id,
        session_id,
        ga_id: getGA4ClientId(),
        initial_utm: getInitialAnalyticsData(),
        current_utm: getUTMs(),
        affiliate_click_id: getCookie(AFFILIATES_COOKIES_CLICK_ID),
        affiliate_click_key: getCookie(AFFILIATES_COOKIES_CLICK_KEY),
      },
      region_id: regionId.value ?? undefined,
      country_code: activeCountry.value?.code ?? undefined,
    });
  }

  async addToCart(variantId: string, quantity: number = 1) {
    return await this._cart.addItem(variantId, quantity);
  }

  async clearCart() {
    const cartItems = this._cart.items;

    for await (const it of cartItems!) {
      await this._cart.removeItem(it.id);
    }

    return this._cart.original;
  }

  async applyDiscountCode(discountCode: string) {
    await this._cart.updateCart({
      discounts: [{ code: discountCode }],
    });
  }

  async getDiscountCode(code: string) {
    return await api.getDiscountByCode(code);
  }

  loadProducts(collections?: Array<ProductsCollection>) {
    return api.loadProducts(collections);
  }

  loadRingProducts(collections?: Array<ProductsCollection>) {
    return api.loadRingProducts(collections);
  }

  async initPaypalPaymentSession(providerId: string) {
    await api.initPaymentSessions(this._cart.id!);
    const data = await api.selectPaymentSession(this._cart.id!, providerId);
    return data.cart.payment_session;
  }

  initPaymentSessions() {
    return api.initPaymentSessions(this._cart.id!);
  }

  updatePaymentSessions(providerId: string, data: unknown) {
    return api.updatePaymentSessions(this._cart.id!, providerId, data);
  }

  applyShippingOptions(optionId: string) {
    return api.applyShippingOptions(this._cart.id!, optionId);
  }

  updateCart(data: CartUpdateParams) {
    return api.updateCart(this._cart.id!, data);
  }

  async completeCart() {
    const response = await api.completeCart(this._cart.id!);
    const searchParams = new URLSearchParams();
    if (this._cart.hash) {
      searchParams.set("hash", this._cart.hash);
    }
    if (response.type === "order") {
      window.open(
        `${CFG.TANGEM_CHECKOUT_URL}/order/confirmed/${response.data.id}?${searchParams.toString()}`,
        "_self",
      );
    } else {
      Sentry?.captureException("Order not created", {
        extra: { e: JSON.stringify(response.data) },
      });
    }
  }

  async tryApplyFreeShipping() {
    return api.checkFreeShipping(this._cart.id!);
  }

  async deletePaymentSession(providerId: string) {
    return api.deletePaymentSession(this._cart.id!, providerId);
  }

  checkout(params: string[][] = []) {
    const urlSearchParams = new URLSearchParams([
      ...this._cart.checkoutParams,
      ...params,
    ]);
    window.open(
      `${CFG.TANGEM_CHECKOUT_URL}/checkout/?${urlSearchParams.toString()}`,
      "_self",
    );
  }

  async applyDiscounts(discountCodes: string[]) {
    if (this._cart.id) {
      return await api.applyMultipleDiscounts(this._cart.id, discountCodes);
    }
  }
}

const tangemStore = new Store();

export default tangemStore;
